/**
 * /admin/neopay-restore — super-admin only.
 * Phase D: read-only diff between NeoPay ledger and Split-Easy DB,
 * then optionally apply a "merge" or "overwrite" reconstruction.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertTriangle, ArrowLeft, ChevronRight, Database, RefreshCw,
  ShieldCheck, Loader2,
} from "lucide-react";

export default function NeoPayRestorePage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [diff, setDiff] = useState(null);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_super_admin) {
      toast.error("Super-admin only");
      navigate("/dashboard");
      return;
    }
    api.get("/neopay/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [authLoading, user, navigate]);

  const runDiff = async () => {
    setRunning(true);
    setDiff(null);
    try {
      const r = await api.post("/neopay/admin/reconstruct");
      setDiff(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Reconstruct diff failed");
    } finally {
      setRunning(false);
    }
  };

  const apply = async (strategy) => {
    if (!window.confirm(
      strategy === "overwrite"
        ? "Overwrite local rows from the ledger? This may replace existing transfer records."
        : "Insert ledger-only transfers into Split-Easy? Existing rows are kept."
    )) return;
    setApplying(true);
    try {
      const r = await api.post("/neopay/admin/reconstruct/apply", { strategy });
      toast.success(`Restored: +${r.data.inserted} new, ${r.data.overwritten} overwritten, ${r.data.skipped} skipped`);
      runDiff();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  if (authLoading) return null;

  return (
    <div className="max-w-5xl lg:max-w-none mx-auto p-4 md:p-6 space-y-4" data-testid="neopay-restore-page">
      <button
        onClick={() => navigate("/admin")}
        className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
      >
        <ArrowLeft className="w-3 h-3" /> back to admin
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Database className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-display font-bold">NeoPay restore</h1>
          <p className="text-xs text-muted-foreground">
            DB-loss recovery: rebuild money-transfer history from the immutable NeoPay audit ledger.
          </p>
        </div>
      </div>

      <Card className="p-4 border-2 rounded-xl">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <div>
              Integration: <span className={status?.enabled ? "text-green-600 font-medium" : "text-amber-500"}>
                {status?.enabled ? "enabled" : "disabled"}
              </span>
              {status?.enabled && (
                <span className="text-muted-foreground"> · health {status?.healthy ? "ok" : "down"}</span>
              )}
            </div>
            {status?.enabled && (
              <div className="text-xs text-muted-foreground font-mono mt-1 break-all">
                {status?.vpa} @ {status?.base_url}
              </div>
            )}
          </div>
          <Button
            size="sm"
            onClick={runDiff}
            disabled={running || !status?.enabled}
            data-testid="run-diff-btn"
          >
            {running ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> scanning…</> : <><RefreshCw className="w-3 h-3 mr-1" /> Run diff</>}
          </Button>
        </div>
      </Card>

      {diff && (
        <Card className="p-4 border-2 rounded-xl space-y-3" data-testid="neopay-restore-diff">
          <h2 className="text-sm font-semibold inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-green-500" /> Diff report
          </h2>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Stat label="ledger scanned" value={diff.ledger_rows_scanned} />
            <Stat label="matched" value={diff.matched} tone="positive" />
            <Stat
              label="drift"
              value={(diff.ledger_only?.length || 0) + (diff.db_only?.length || 0)}
              tone={(diff.ledger_only?.length || diff.db_only?.length) ? "negative" : "neutral"}
            />
          </div>

          <Section
            title="On ledger but missing locally"
            items={diff.ledger_only || []}
            renderItem={(r) => (
              <>
                <span className="font-mono text-xs">{r.parsed?.transfer_id}</span>
                <ChevronRight className="w-3 h-3 inline mx-1" />
                <span>amount {r.parsed?.amount}</span>
                <span className="ml-2 text-muted-foreground">block #{r.block_number ?? "?"}</span>
              </>
            )}
          />

          <Section
            title="Approved locally but never on ledger"
            items={diff.db_only || []}
            renderItem={(r) => (
              <>
                <span className="font-mono text-xs">{r.id}</span>
                <ChevronRight className="w-3 h-3 inline mx-1" />
                <span>amount {r.amount}</span>
                <span className="ml-2 text-muted-foreground">approved {r.approved_at?.slice(0, 10)}</span>
              </>
            )}
          />

          {(diff.ledger_only?.length || 0) > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <div className="flex items-center text-xs text-amber-500 mr-auto inline-flex gap-1">
                <AlertTriangle className="w-3 h-3" /> reconstruction is destructive — back up Mongo first
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={applying}
                onClick={() => apply("merge")}
                data-testid="apply-merge-btn"
              >
                Insert missing
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={applying}
                onClick={() => apply("overwrite")}
                data-testid="apply-overwrite-btn"
              >
                Overwrite local
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const toneCls =
    tone === "positive" ? "text-green-600" :
    tone === "negative" ? "text-amber-500" : "";
  return (
    <div className="border rounded-lg p-2 text-center">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-lg font-display font-bold ${toneCls}`} data-testid={`stat-${label.replace(/\s+/g, "-")}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

function Section({ title, items, renderItem }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-xs font-semibold mb-1">{title} ({items.length})</div>
      <div className="space-y-1 max-h-56 overflow-y-auto">
        {items.slice(0, 100).map((r, i) => (
          <div key={i} className="text-xs border rounded-md p-1.5 bg-muted/20" data-testid={`diff-row-${i}`}>
            {renderItem(r)}
          </div>
        ))}
      </div>
    </div>
  );
}
